import { useQueryClient } from '@tanstack/react-query';
import { Image as ImagemIcone, Loader2, MoreHorizontal, Package, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { useRef, useState } from 'react';

import { useParceirosAdminControllerListar } from '@/api/generated/admin-parceiros/admin-parceiros';
import {
  getProdutosAdminControllerListarQueryKey,
  useProdutosAdminControllerDefinirAtivo,
  useProdutosAdminControllerEnviarImagem,
  useProdutosAdminControllerListar,
} from '@/api/generated/admin-produtos-de-parceiros/admin-produtos-de-parceiros';
import type { ProdutoAdminResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ProdutoFormDialogo } from '@/features/admin/produtos-parceiros/components/ProdutoFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { RecorteDeImagemDialogo } from '@/shared/components/RecorteDeImagemDialogo';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Produtos de parceiros (B8-6).
 *
 * ⚠️ Sem excluir: `ReceituarioItem` referencia o produto, e apagar um produto
 * já importado quebraria receitas antigas. Desativar tira da vitrine e do
 * seletor do receituário sem tocar no histórico.
 */
export function ProdutosParceirosPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [parceiroId, setParceiroId] = useState(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ProdutoAdminResponse | undefined>();
  const [imagemDe, setImagemDe] = useState<{ produtoId: string; arquivo: File } | null>(null);

  /**
   * O seletor de arquivo mora AQUI, não no item de menu.
   *
   * ⚠️ Dentro do `DropdownMenuContent` ele não funciona: abrir o seletor nativo
   * tira o foco da janela, o Radix fecha o menu, o `<input>` desmonta — e o
   * `change` do arquivo escolhido não chega em React nenhum. O clique some sem
   * erro e sem efeito. No nível da página o input sobrevive ao menu.
   */
  const entradaDeImagem = useRef<HTMLInputElement>(null);
  const produtoDaImagem = useRef<string | null>(null);

  const { data, isPending, isError, refetch } = useProdutosAdminControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(parceiroId !== TODOS ? { parceiroId } : {}),
  });

  // A lista de parceiros alimenta o filtro e o `<select>` do formulário.
  const { data: parceiros } = useParceirosAdminControllerListar({
    pagina: 1,
    porPagina: 100,
    ordenarPor: 'ordem',
    ordem: 'asc',
  });

  const definirAtivo = useProdutosAdminControllerDefinirAtivo();
  const enviarImagem = useProdutosAdminControllerEnviarImagem();
  const podeCriar = pode('admin_produtos_parceiros.criar');
  const podeEditar = pode('admin_produtos_parceiros.editar');

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: getProdutosAdminControllerListarQueryKey() });
  }

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Package className="w-7 h-7 text-brand" />
            Produtos de parceiros
          </h2>
          <p className="text-muted-foreground mt-1">
            O que o veterinário pode importar para o receituário.
          </p>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
            className="gap-2 bg-brand text-brand-foreground hover:bg-brand/90"
          >
            <Plus className="w-4 h-4" />
            Novo produto
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Buscar por nome ou categoria..."
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPagina(1);
          }}
          className="max-w-sm"
        />

        <Select
          value={parceiroId}
          onValueChange={(v) => {
            setParceiroId(v);
            setPagina(1);
          }}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Todos os parceiros" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos os parceiros</SelectItem>
            {parceiros?.itens.map((parceiro) => (
              <SelectItem key={parceiro.id} value={parceiro.id}>
                {parceiro.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isPending ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : data.itens.length === 0 ? (
        <EstadoVazio icone={Package} titulo="Nenhum produto" descricao="Cadastre o primeiro." />
      ) : (
        <>
          <div className="rounded-xl border border-border overflow-hidden bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Parceiro</TableHead>
                  <TableHead className="text-center">Importações</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data.itens.map((produto) => (
                  <TableRow key={produto.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-white border border-border flex items-center justify-center shrink-0 overflow-hidden">
                          {produto.imagemUrl ? (
                            <img
                              src={produto.imagemUrl}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Package className="w-4 h-4 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{produto.nome}</p>
                          {produto.categoria && (
                            <p className="text-xs text-muted-foreground truncate">
                              {produto.categoria}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-sm text-muted-foreground">
                      {produto.parceiroNome}
                    </TableCell>

                    <TableCell className="text-center">{produto.totalImportacoes}</TableCell>

                    <TableCell>
                      {produto.ativo ? (
                        <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </TableCell>

                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Ações">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {podeEditar && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEmEdicao(produto);
                                  setFormAberto(true);
                                }}
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>

                              <EnvioDeImagem
                                enviando={
                                  enviarImagem.isPending && imagemDe?.produtoId === produto.id
                                }
                                aoAbrir={() => {
                                  produtoDaImagem.current = produto.id;
                                  entradaDeImagem.current?.click();
                                }}
                              />

                              <DropdownMenuItem
                                onClick={() =>
                                  definirAtivo.mutate(
                                    { id: produto.id, data: { ativo: !produto.ativo } },
                                    {
                                      onSuccess: invalidar,
                                      onError: (erro) =>
                                        toast.error(
                                          erro instanceof ApiError
                                            ? erro.message
                                            : 'Não foi possível alterar',
                                        ),
                                    },
                                  )
                                }
                              >
                                {produto.ativo ? (
                                  <>
                                    <PowerOff className="w-4 h-4 mr-2" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <Power className="w-4 h-4 mr-2" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Paginacao
            pagina={data.pagina}
            totalPaginas={data.totalPaginas}
            total={data.total}
            porPagina={data.porPagina}
            aoMudarPagina={setPagina}
          />
        </>
      )}

      {formAberto && (
        <ProdutoFormDialogo
          aberto
          aoFechar={() => {
            setFormAberto(false);
            setEmEdicao(undefined);
          }}
          parceiros={parceiros?.itens ?? []}
          {...(emEdicao ? { produto: emEdicao } : {})}
        />
      )}

      <input
        ref={entradaDeImagem}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        aria-label="Arquivo de imagem do produto"
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          // Zerar permite escolher o MESMO arquivo de novo depois de cancelar.
          e.target.value = '';
          const produtoId = produtoDaImagem.current;
          if (arquivo && produtoId) setImagemDe({ produtoId, arquivo });
        }}
      />

      {imagemDe && (
        <RecorteDeImagemDialogo
          aberto
          aoFechar={() => setImagemDe(null)}
          arquivo={imagemDe.arquivo}
          proporcao={1}
          larguraSaida={800}
          encaixar
          enviando={enviarImagem.isPending}
          aoConfirmar={(recortado) =>
            enviarImagem.mutate(
              { id: imagemDe.produtoId, data: { arquivo: recortado } },
              {
                onSuccess: () => {
                  setImagemDe(null);
                  invalidar();
                  toast.success('Imagem atualizada');
                },
                // A mensagem do servidor já explica a regra com as medidas.
                onError: (erro) =>
                  toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar'),
              },
            )
          }
        />
      )}
    </div>
  );
}

/**
 * Só o item de menu. O `<input type="file">`, o enquadramento e o envio moram
 * na página — ver o comentário em `entradaDeImagem`, que explica por que nada
 * disso pode ficar aqui dentro.
 *
 * `onSelect` com `preventDefault` porque o menu fecharia antes do clique chegar
 * ao input, e o seletor nunca abriria.
 */
function EnvioDeImagem({ enviando, aoAbrir }: { enviando: boolean; aoAbrir: () => void }) {
  return (
    <DropdownMenuItem
      onSelect={(evento) => {
        evento.preventDefault();
        aoAbrir();
      }}
    >
      {enviando ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <ImagemIcone className="w-4 h-4 mr-2" />
      )}
      Imagem (1:1)
    </DropdownMenuItem>
  );
}
